import React from "react";
import { Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import Markdown from "markdown-to-jsx";

const PRIMARY = "#5C149B";

// Enlaces en una pestaña nueva; el PDF firmado de un reporte se muestra como «Abrir archivo» en vez de la URL larga.
const AssistantLink = ({ href, children, ...props }) => {
    const bareUrl = React.Children.toArray(children).join("") === href;
    return (
        <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
            {bareUrl && `${href}`.includes("/api/v1/files") ? "Abrir archivo" : children}
        </a>
    );
};

// Una tabla ancha se desplaza dentro de la burbuja en vez de romper el panel.
const AssistantTable = ({ children, ...props }) => (
    <Box sx={{ overflowX: "auto", my: 1 }}>
        <table {...props}>{children}</table>
    </Box>
);

// Un guion bajo dentro de una palabra (count_in, AI_PROVIDER) es texto y no abre una cursiva, como en GitHub. Las URL
// quedan intactas.
const URL_PATTERN = /(https?:\/\/[^\s)]+)/g;
const escapeIntrawordUnderscores = (text) => text.split(URL_PATTERN)
    .map((part, index) => (index % 2 === 1 ? part : part.replace(/([A-Za-z0-9])_(?=[A-Za-z0-9])/g, "$1\\_")))
    .join("");

const OPTIONS = {
    forceBlock: true,
    // Lo que escribe el modelo se muestra como Markdown, nunca como HTML.
    disableParsingRawHTML: true,
    overrides: {
        a: { component: AssistantLink },
        table: { component: AssistantTable },
    },
};

// Respuestas de Xpit Assistant en la web: Markdown simple (párrafos, negritas, listas, tablas cortas y enlaces), con
// la tipografía del chat.
const AssistantMarkdown = ({ children }) => (
    <Box
        sx={{
            fontSize: 14,
            lineHeight: 1.5,
            overflowWrap: "anywhere",
            "& p": { m: 0, mb: 1 },
            "& > *:last-child, & p:last-child": { mb: 0 },
            "& ul, & ol": { m: 0, mb: 1, pl: 2.5 },
            "& li": { mb: 0.25 },
            "& li > p": { m: 0 },
            "& strong": { fontWeight: 700, color: "text.primary" },
            "& em": { color: "text.secondary" },
            "& a": { color: PRIMARY, fontWeight: 600, textDecoration: "none", "&:hover": { textDecoration: "underline" } },
            "& h1, & h2, & h3, & h4, & h5, & h6": { fontSize: 14, fontWeight: 700, m: 0, mb: 0.5 },
            "& table": { width: "100%", borderCollapse: "collapse", fontSize: 13 },
            "& th, & td": {
                textAlign: "left",
                px: 1,
                py: 0.6,
                borderBottom: `1px solid ${alpha("#0F172A", 0.08)}`,
                whiteSpace: "nowrap",
            },
            "& td:first-of-type": { whiteSpace: "normal", minWidth: 96 },
            "& th": { fontSize: 12, fontWeight: 700, color: "text.secondary", bgcolor: alpha(PRIMARY, 0.05) },
            "& tr:last-child td": { borderBottom: "none" },
            "& code": { fontFamily: "monospace", fontSize: 12.5, px: 0.5, borderRadius: 1, bgcolor: alpha("#0F172A", 0.05) },
            "& hr": { border: "none", borderTop: `1px solid ${alpha("#0F172A", 0.08)}`, my: 1 },
        }}>
        <Markdown options={OPTIONS}>{escapeIntrawordUnderscores(`${children || ""}`)}</Markdown>
    </Box>
);

export default AssistantMarkdown;
